import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Award, Target, DollarSign, 
  Users, Calendar, ChevronDown, ChevronUp, BarChart3,
  PieChart, Activity, Zap, Star, Crown
} from 'lucide-react';

interface PerformerMetrics {
  label: string;
  value: number;
  sublabel?: string;
  // Extended analytics data
  revenue?: number;
  deals?: number;
  conversionRate?: number;
  clientSatisfaction?: number;
  monthlyTrend?: number[];
  topProducts?: { name: string; value: number }[];
  topClients?: { name: string; value: number }[];
}

interface AdvancedPerformerAnalyticsProps {
  title: string;
  data: PerformerMetrics[];
}

const AdvancedPerformerAnalytics: React.FC<AdvancedPerformerAnalyticsProps> = ({ title, data }) => {
  const [expandedPerformer, setExpandedPerformer] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'detailed'>('cards');

  // Sort by performance
  const sortedData = [...data].sort((a, b) => b.value - a.value);

  // Generate mock detailed data for demonstration
  const getDetailedData = (performer: PerformerMetrics, index: number) => ({
    ...performer,
    revenue: performer.revenue || Math.floor(Math.random() * 500000) + 100000,
    deals: performer.deals || Math.floor(Math.random() * 50) + 10,
    conversionRate: performer.conversionRate || Math.floor(Math.random() * 40) + 60,
    clientSatisfaction: performer.clientSatisfaction || Math.floor(Math.random() * 20) + 80,
    monthlyTrend: performer.monthlyTrend || Array.from({ length: 6 }, () => Math.floor(Math.random() * 100)),
    topProducts: performer.topProducts || [
      { name: 'Product A', value: Math.floor(Math.random() * 100) },
      { name: 'Product B', value: Math.floor(Math.random() * 80) },
      { name: 'Product C', value: Math.floor(Math.random() * 60) },
    ],
    topClients: performer.topClients || [
      { name: 'Client X', value: Math.floor(Math.random() * 100000) },
      { name: 'Client Y', value: Math.floor(Math.random() * 80000) },
      { name: 'Client Z', value: Math.floor(Math.random() * 60000) },
    ],
  });

  const getRankBadge = (index: number) => {
    const badges = [
      { icon: Crown, color: 'from-yellow-400 to-amber-500', text: 'Champion', glow: 'shadow-yellow-500/50' },
      { icon: Award, color: 'from-gray-300 to-gray-400', text: 'Elite', glow: 'shadow-gray-400/50' },
      { icon: Star, color: 'from-orange-400 to-orange-500', text: 'Star', glow: 'shadow-orange-500/50' },
      { icon: Target, color: 'from-blue-400 to-blue-500', text: 'Achiever', glow: 'shadow-blue-500/50' },
    ];
    return badges[Math.min(index, 3)];
  };

  return (
    <div className="space-y-6">
      {/* Header with View Toggle */}
      <div className="flex items-center justify-between">
        <h3 className="text-2xl font-bold text-gray-900">{title}</h3>
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setViewMode('cards')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'cards'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <BarChart3 className="w-4 h-4 inline mr-2" />
            Overview
          </button>
          <button
            onClick={() => setViewMode('detailed')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'detailed'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <PieChart className="w-4 h-4 inline mr-2" />
            Analytics
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {viewMode === 'cards' ? (
          <motion.div
            key="cards"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {sortedData.map((performer, index) => {
              const badge = getRankBadge(index);
              const Icon = badge.icon;
              const detailedData = getDetailedData(performer, index);

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9, rotateY: -15 }}
                  animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ scale: 1.05, y: -10 }}
                  className="relative"
                >
                  {/* Rank Badge */}
                  <div className={`absolute -top-3 -right-3 z-10 w-14 h-14 rounded-full 
                    bg-gradient-to-br ${badge.color} flex items-center justify-center 
                    shadow-lg ${badge.glow}`}
                  >
                    <Icon className="w-7 h-7 text-white" />
                  </div>

                  {/* Card */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-100 
                    hover:border-blue-300 transition-all duration-300 h-full"
                  >
                    {/* Rank */}
                    <div className="text-5xl font-black text-gray-200 mb-2">
                      #{index + 1}
                    </div>

                    {/* Name */}
                    <h4 className="text-lg font-bold text-gray-900 mb-3 truncate">
                      {performer.label}
                    </h4>

                    {/* Score Circle */}
                    <div className="relative w-32 h-32 mx-auto mb-4">
                      <svg className="transform -rotate-90 w-32 h-32">
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke="#E5E7EB"
                          strokeWidth="8"
                          fill="none"
                        />
                        <motion.circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke={`url(#gradient-${index})`}
                          strokeWidth="8"
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={`${2 * Math.PI * 56}`}
                          initial={{ strokeDashoffset: 2 * Math.PI * 56 }}
                          animate={{
                            strokeDashoffset: 2 * Math.PI * 56 * (1 - performer.value / 100)
                          }}
                          transition={{ duration: 1.5, delay: index * 0.1 }}
                        />
                        <defs>
                          <linearGradient id={`gradient-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor={badge.color.includes('yellow') ? '#FBBF24' :
                              badge.color.includes('gray') ? '#9CA3AF' :
                              badge.color.includes('orange') ? '#FB923C' : '#60A5FA'} />
                            <stop offset="100%" stopColor={badge.color.includes('yellow') ? '#F59E0B' :
                              badge.color.includes('gray') ? '#6B7280' :
                              badge.color.includes('orange') ? '#F97316' : '#3B82F6'} />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center">
                          <div className="text-3xl font-black text-gray-900">{performer.value}</div>
                          <div className="text-xs text-gray-500">Score</div>
                        </div>
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-blue-50 rounded-lg p-2 text-center">
                        <DollarSign className="w-4 h-4 mx-auto text-blue-600 mb-1" />
                        <div className="font-bold text-gray-900">
                          ${(detailedData.revenue / 1000).toFixed(0)}K
                        </div>
                        <div className="text-gray-500">Revenue</div>
                      </div>
                      <div className="bg-green-50 rounded-lg p-2 text-center">
                        <Target className="w-4 h-4 mx-auto text-green-600 mb-1" />
                        <div className="font-bold text-gray-900">{detailedData.deals}</div>
                        <div className="text-gray-500">Deals</div>
                      </div>
                    </div>

                    {/* Badge */}
                    <div className="mt-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold
                        bg-gradient-to-r ${badge.color} text-white`}>
                        {badge.text}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            key="detailed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-4"
          >
            {sortedData.map((performer, index) => {
              const badge = getRankBadge(index);
              const Icon = badge.icon;
              const detailedData = getDetailedData(performer, index);
              const isExpanded = expandedPerformer === index;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                >
                  {/* Header - Always Visible */}
                  <div
                    onClick={() => setExpandedPerformer(isExpanded ? null : index)}
                    className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {/* Rank Badge */}
                        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${badge.color}
                          flex items-center justify-center shadow-lg ${badge.glow}`}>
                          <Icon className="w-8 h-8 text-white" />
                        </div>

                        {/* Info */}
                        <div>
                          <div className="flex items-center gap-3">
                            <h4 className="text-xl font-bold text-gray-900">{performer.label}</h4>
                            <span className="text-sm text-gray-500">#{index + 1}</span>
                          </div>
                          <p className="text-sm text-gray-500">{performer.sublabel}</p>
                        </div>
                      </div>

                      {/* Summary Stats */}
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-gray-900">{performer.value}%</div>
                          <div className="text-xs text-gray-500">Performance</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">
                            ${(detailedData.revenue / 1000).toFixed(0)}K
                          </div>
                          <div className="text-xs text-gray-500">Revenue</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{detailedData.deals}</div>
                          <div className="text-xs text-gray-500">Deals</div>
                        </div>

                        {/* Expand Button */}
                        <motion.button
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                        >
                          <ChevronDown className="w-5 h-5 text-gray-600" />
                        </motion.button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-t border-gray-200 bg-gradient-to-br from-gray-50 to-white"
                      >
                        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                          {/* Left Column - Performance Metrics */}
                          <div className="space-y-4">
                            <h5 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <Activity className="w-5 h-5 text-blue-600" />
                              Performance Metrics
                            </h5>

                            {/* Conversion Rate */}
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-gray-600">Conversion Rate</span>
                                <span className="text-lg font-bold text-gray-900">
                                  {detailedData.conversionRate}%
                                </span>
                              </div>
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <motion.div
                                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${detailedData.conversionRate}%` }}
                                  transition={{ duration: 1, delay: 0.2 }}
                                />
                              </div>
                            </div>

                            {/* Client Satisfaction */}
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm text-gray-600">Client Satisfaction</span>
                                <span className="text-lg font-bold text-gray-900">
                                  {detailedData.clientSatisfaction}%
                                </span>
                              </div>
                              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                <motion.div
                                  className="h-full bg-gradient-to-r from-green-500 to-green-600"
                                  initial={{ width: 0 }}
                                  animate={{ width: `${detailedData.clientSatisfaction}%` }}
                                  transition={{ duration: 1, delay: 0.3 }}
                                />
                              </div>
                            </div>

                            {/* Monthly Trend Sparkline */}
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                              <div className="text-sm text-gray-600 mb-3">6-Month Trend</div>
                              <div className="flex items-end gap-1 h-20">
                                {detailedData.monthlyTrend.map((value, i) => (
                                  <motion.div
                                    key={i}
                                    className="flex-1 bg-gradient-to-t from-purple-500 to-purple-400 rounded-t"
                                    initial={{ height: 0 }}
                                    animate={{ height: `${value}%` }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                  />
                                ))}
                              </div>
                            </div>
                          </div>

                          {/* Middle Column - Top Products Pie Chart */}
                          <div className="space-y-4">
                            <h5 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <PieChart className="w-5 h-5 text-purple-600" />
                              Top Products
                            </h5>

                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                              {/* Donut Chart */}
                              <div className="relative w-48 h-48 mx-auto mb-4">
                                <svg className="transform -rotate-90 w-48 h-48">
                                  {detailedData.topProducts.map((product, i) => {
                                    const total = detailedData.topProducts.reduce((sum, p) => sum + p.value, 0);
                                    const percentage = (product.value / total) * 100;
                                    const circumference = 2 * Math.PI * 70;
                                    const offset = detailedData.topProducts
                                      .slice(0, i)
                                      .reduce((sum, p) => sum + (p.value / total) * circumference, 0);

                                    const colors = ['#3B82F6', '#10B981', '#F59E0B'];

                                    return (
                                      <motion.circle
                                        key={i}
                                        cx="96"
                                        cy="96"
                                        r="70"
                                        stroke={colors[i]}
                                        strokeWidth="24"
                                        fill="none"
                                        strokeDasharray={circumference}
                                        initial={{ strokeDashoffset: circumference }}
                                        animate={{
                                          strokeDashoffset: circumference - (percentage / 100) * circumference,
                                          rotate: (offset / circumference) * 360
                                        }}
                                        transition={{ duration: 1, delay: i * 0.2 }}
                                        style={{ transformOrigin: '96px 96px' }}
                                      />
                                    );
                                  })}
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="text-center">
                                    <div className="text-2xl font-bold text-gray-900">
                                      {detailedData.topProducts.length}
                                    </div>
                                    <div className="text-xs text-gray-500">Products</div>
                                  </div>
                                </div>
                              </div>

                              {/* Legend */}
                              <div className="space-y-2">
                                {detailedData.topProducts.map((product, i) => {
                                  const colors = ['bg-blue-500', 'bg-green-500', 'bg-amber-500'];
                                  const total = detailedData.topProducts.reduce((sum, p) => sum + p.value, 0);
                                  const percentage = ((product.value / total) * 100).toFixed(1);

                                  return (
                                    <div key={i} className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <div className={`w-3 h-3 rounded-full ${colors[i]}`} />
                                        <span className="text-sm text-gray-700">{product.name}</span>
                                      </div>
                                      <span className="text-sm font-semibold text-gray-900">
                                        {percentage}%
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </div>

                          {/* Right Column - Top Clients */}
                          <div className="space-y-4">
                            <h5 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                              <Users className="w-5 h-5 text-green-600" />
                              Top Clients
                            </h5>

                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 space-y-3">
                              {detailedData.topClients.map((client, i) => {
                                const maxValue = Math.max(...detailedData.topClients.map(c => c.value));
                                const percentage = (client.value / maxValue) * 100;

                                return (
                                  <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                    className="space-y-2"
                                  >
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm font-medium text-gray-700">
                                        {client.name}
                                      </span>
                                      <span className="text-sm font-bold text-gray-900">
                                        ${(client.value / 1000).toFixed(0)}K
                                      </span>
                                    </div>
                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                      <motion.div
                                        className="h-full bg-gradient-to-r from-green-500 to-emerald-600"
                                        initial={{ width: 0 }}
                                        animate={{ width: `${percentage}%` }}
                                        transition={{ duration: 1, delay: i * 0.1 }}
                                      />
                                    </div>
                                  </motion.div>
                                );
                              })}
                            </div>

                            {/* Additional Stats */}
                            <div className="grid grid-cols-2 gap-3">
                              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 text-center">
                                <Zap className="w-5 h-5 mx-auto text-blue-600 mb-1" />
                                <div className="text-lg font-bold text-gray-900">
                                  {detailedData.conversionRate}%
                                </div>
                                <div className="text-xs text-gray-600">Win Rate</div>
                              </div>
                              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-3 text-center">
                                <Calendar className="w-5 h-5 mx-auto text-purple-600 mb-1" />
                                <div className="text-lg font-bold text-gray-900">
                                  {Math.floor(detailedData.deals / 4)}
                                </div>
                                <div className="text-xs text-gray-600">Avg/Week</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdvancedPerformerAnalytics;

